import { getP } from 'redux-polyglot'
import api from '../libs/api'
import { getCartItems, getCartReference } from '../selectors/cart'
import { getPackage } from '../selectors/packages'
import { isReady as isCartReady } from '../selectors/cart'
import { loadCart } from './cart'

/**
 * Actions for fetching list of packages
 */
export const FETCH_PACKAGES_REQUEST = 'PACKAGES/FETCH_PACKAGES_REQUEST'
export const FETCH_PACKAGES_SUCCESS = 'PACKAGES/FETCH_PACKAGES_SUCCESS'
export const FETCH_PACKAGES_FAILURE = 'PACKAGES/FETCH_PACKAGES_FAILURE'

const fetchPackagesRequest = () => ({
  type: FETCH_PACKAGES_REQUEST
})

const fetchPackagesSuccess = (plans, cartItems, preFocusedId) => {
  let packages = []
  let focusedPackageId = -1
  plans.forEach((plan) => {
    packages.push({
      id: plan.id,
      name: plan.name,
      description: plan.description,
      price: plan.price,
      recurring: plan.recurring,
      recurDetails: {
        length: plan.recurring_length,
        type: plan.recurring_type
      },
      isSelected: cartItems.find(item => item.itemId === plan.id && item.itemType === "channel_group") !== undefined,
      hasAccess: plan.has_subscription,
      type: 'package',
      detailsRequested: false
    })
    if (plan.id.toString() === preFocusedId) {
      focusedPackageId = plan.id
    }
  })
  if (focusedPackageId === -1 && packages.length > 0) {
    focusedPackageId = packages[0].id
  }
  return {
    type: FETCH_PACKAGES_SUCCESS,
    payload: {
      packages,
      focusedPackageId
    }
  }
}

const fetchPackagesFailure = (error) => ({
  type: FETCH_PACKAGES_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const realLoadPackages = (preFocusedId) => (dispatch, getState) => {
  dispatch(fetchPackagesRequest())
  return api.getPackages()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchPackagesSuccess(response.plans, getCartItems(getState()), preFocusedId))
        } else {
          return dispatch(fetchPackagesFailure(response.reason))
        }
      },
      () => dispatch(fetchPackagesFailure(getP(getState()).t("checkout_app.error_loading_packages", { _: "An error has occurred while loading packages" })))
    )
}

export const loadPackages = (preFocusedId) => (dispatch, getState) => {
  if (isCartReady(getState())) {
    return dispatch(realLoadPackages(preFocusedId))
  } else {
    return dispatch(loadCart())
      .then(() =>
        dispatch(realLoadPackages(preFocusedId))
      )
  }
}

/**
 * Actions for fetching details of a particular package
 */
export const FETCH_PACKAGE_REQUEST = 'PACKAGES/FETCH_PACKAGE_REQUEST'
export const FETCH_PACKAGE_SUCCESS = 'PACKAGES/FETCH_PACKAGE_SUCCESS'
export const FETCH_PACKAGE_FAILURE = 'PACKAGES/FETCH_PACKAGE_FAILURE'

const fetchPackageRequest = (packageId) => ({
  type: FETCH_PACKAGE_REQUEST,
  payload: {
    id: packageId
  }
})

const fetchPackageSuccess = (pkg) => {
  let items = []
  pkg.live_channels.forEach((channel) => {
    items.push({
      id: channel.id,
      name: channel.name,
      thumb: channel.img.small
    })
  })
  if (items.length < 12 && pkg.catchup_channels) {
    pkg.catchup_channels.forEach((channel) => {
      items.push({
        id: channel.id,
        name: channel.name,
        thumb: channel.img.small
      })
    })
  }
  return {
    type: FETCH_PACKAGE_SUCCESS,
    payload: {
      id: pkg.id,
      items: items
    }
  }
}

const fetchPackageFailure = (error) => ({
  type: FETCH_PACKAGE_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const loadPackage = id => (dispatch, getState) => {
  dispatch(fetchPackageRequest(id))
  return api.getPackage(id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchPackageSuccess(response.details))
        } else {
          return dispatch(fetchPackageFailure(response.reason))
        }
      },
      () => dispatch(fetchPackageFailure(getP(getState()).t("checkout_app.error_loading_package_details", { _: "An error has occurred while loading package details" })))
    )
}

/**
 * Actions for selecting a package
 */
export const SELECT_PACKAGE_REQUEST = 'PACKAGES/SELECT_PACKAGE_REQUEST'
export const SELECT_PACKAGE_SUCCESS = 'PACKAGES/SELECT_PACKAGE_SUCCESS'
export const SELECT_PACKAGE_FAILURE = 'PACKAGES/SELECT_PACKAGE_FAILURE'

const selectPackageRequest = id => ({
  type: SELECT_PACKAGE_REQUEST,
  payload: {
    id
  }
})

const selectPackageSuccess = (pkg, cart) => {
  return {
    type: SELECT_PACKAGE_SUCCESS,
    payload: {
      pkg,
      cart
    }
  }
}

const selectPackageFailure = error => ({
  type: SELECT_PACKAGE_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const selectPackage = (id, triggerDispatch = true) => (dispatch, getState) => {
  triggerDispatch && dispatch(selectPackageRequest(id))
  const cartReference = getCartReference(getState())
  return api.addItemToCart(cartReference, id, 'channel_group')
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return triggerDispatch ? dispatch(selectPackageSuccess(getPackage(id, getState()), response.data)) : Promise.resolve()
        } else {
          return triggerDispatch ? dispatch(selectPackageFailure(response.reason)) : Promise.resolve()
        }
      },
      () => triggerDispatch ? dispatch(selectPackageFailure(getP(getState()).t("checkout_app.error_adding_package", { _: "An error has occurred while adding the package to the cart" }))) : Promise.resolve()
    )
}

/**
 * Actions for focusing inside the package list
 */
export const FOCUS_PACKAGE = 'PACKAGES/FOCUS_PACKAGE'

export const focusPackage = (id) => ({
  type: FOCUS_PACKAGE,
  payload: {
    id
  }
})

/**
 * Actions for focusing the details screen
 */
export const FOCUS_DETAILS = 'PACKAGES/FOCUS_DETAILS'

export const focusDetails = () => ({
  type: FOCUS_DETAILS
})

/**
 * Actions for dismissing the error screen
 */
export const DISMISS_ERROR = 'PACKAGES/DISMISS_ERROR'

export const dismissError = () => ({
  type: DISMISS_ERROR
})

/**
 * Actions for dealing with the Paypal subscription error
 */
export const SHOW_PAYPAL_SUBSCRIPTION_ERROR = 'PACKAGES/SHOW_PAYPAL_SUBSCRIPTION_ERROR'
export const DISMISS_PAYPAL_SUBSCRIPTION_ERROR = 'PACKAGES/DIMISS_PAYPAL_SUBSCRIPTION_ERROR'

export const showPaypalSubscriptionError = () => ({
  type: SHOW_PAYPAL_SUBSCRIPTION_ERROR
})
export const dismissPaypalSubscriptionError = () => ({
  type: DISMISS_PAYPAL_SUBSCRIPTION_ERROR
})

/**
 * Actions for adding a package to cart from any page
 */
export const RESET = 'CHECKOUT/PACKAGES/RESET'
export const selectPackageAndClear = id => dispatch => {
  return dispatch(selectPackage(id, false)).then(dispatch({ type: RESET }))
}
